import { Box, Button, HStack, Icon, Text, Tooltip, VStack, useToast } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TbArrowMerge, TbInfoCircle } from 'react-icons/tb';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type { InvoicingSettings } from '../../../../../hooks/useGetSettings';
import { useUpdateSettings } from '../../../../../hooks/useUpdateSettings';
import { useGetMakes } from '../../../../../hooks/useGetMakes';
import {
    useRelatedInsurance,
    useSearchInsurance,
    type InsuranceSearchResult,
} from '../../../../../hooks/useSearchInsurance';
import type { SupplierSearchResult } from '../../../../../hooks/useSearchSuppliers';
import {
    SettingsCard,
    SettingsCheckTable,
    SettingsFlow,
    SettingsFlowSplit,
    SettingsNumberInput,
    SettingsRow,
    useSettingsDetailCrumbs,
} from '../../../../../components/settings/primitives';
import CarrierSearchField from '../../../../../components/settings/CarrierSearchField';
import SubCompanyTable, {
    type SubCompanyRow,
} from '../../../../../components/settings/SubCompanyTable';
import { Select, TextField } from '../../../../../ui';
import { invoicingSettingsForApi } from '../../../../../utils/invoicingSettingsApi';
import {
    conflictingLkqPricingCompanies,
    groupLkqPricingRules,
    removeLkqPricingRule,
} from '../../../../../utils/lkqPricingRows';
import { LKQ_PRICING_WILDCARD } from '../../../../../utils/lkqPricingConstants';
import VendorSearchField from '../../vendors/VendorSearchField';
import PricingRuleSteps from './PricingRuleSteps';
import {
    ALL_SELECTION,
    buildPricingRuleDraft,
    buildPricingRuleEntries,
    isAllCompanies,
    isAllStates,
    isAllVendors,
    pricingRuleFollowsName,
    pricingRuleValues,
    seedPricingRuleSubModes,
    type PricingRuleDraft,
    type PricingRuleSubMode,
} from './pricingRuleDraft';
import {
    parsePricingRowId,
    percentageOf as percentageOfSpec,
    pricingEntriesOf,
    pricingEntryFactory,
    pricingSettingsWith,
    pricingTableSpec,
    type PricingEntry,
    type PricingSectionId,
    type PricingTableSpec,
} from './pricingTables';

/** Sentinel id for the "All States" row in the states table. */
const ALL_STATES_ROW = '__all__';

/** Sentence case for a noun used at the start of a toast title. */
const capitalize = (noun: string): string => noun.charAt(0).toUpperCase() + noun.slice(1);

interface PricingRuleEditorProps {
    /** Which table's rule is being edited. */
    spec: PricingTableSpec;
    /** The rule's key, or 'new' for the add case. */
    ruleKey: string;
    /** Where Cancel and a bad rule key go back to. */
    sectionPath: string;
    /** The invoicing section's data from the settings response. */
    data: InvoicingSettings;
    /** States the org actually operates in, from its locations. */
    organizationStates: string[];
    refetch?: (options?: RefetchOptions) => Promise<QueryObserverResult<unknown, Error>>;
}

/**
 * Add or edit one pricing rule, full screen and on one page: what the rule is
 * made of on the left, the rule itself read back as steps on the right.
 * Replaces the two-pane markup, discount and discount flag modals, so the same
 * screen serves adding and editing, every one of those tables gets the same
 * screen, and the sub companies and states tables get the height they need.
 *
 * A rule is stored as one entry per company, vendor and state — that is what
 * the invoicing pipeline looks a line item up by — so saving writes the whole
 * invoicing subdocument through POST /settings/update and the merge happens in
 * pricingRuleDraft. Which table is being edited comes in as `spec`, resolved
 * from the section and the tab in the route, and is the only thing that differs
 * between them.
 *
 * Insurance companies work the way DRP carriers do: a company and the sub
 * companies riding along on its rule are one row, and branching one gives it a
 * rule — and a row — of its own. When a group mate already has a rule, folding
 * into it is offered first, which is almost always what a shop means when it
 * sets the same terms for a second name from one group.
 */
const PricingRuleEditor = ({
    spec,
    ruleKey,
    sectionPath,
    data,
    organizationStates,
    refetch,
}: PricingRuleEditorProps) => {
    const navigate = useNavigate();
    const toast = useToast();
    const updateSettings = useUpdateSettings();

    const percentageOf = useMemo(() => percentageOfSpec(spec), [spec]);
    const buildEntry = useMemo(() => pricingEntryFactory(spec), [spec]);

    const entries = useMemo(() => pricingEntriesOf(spec, data), [spec, data]);
    const rules = useMemo(() => groupLkqPricingRules(entries, percentageOf), [entries, percentageOf]);

    const isAddMode = ruleKey === 'new';
    const rule = useMemo(
        () => (isAddMode ? undefined : rules.find((candidate) => candidate.ruleKey === ruleKey)),
        [isAddMode, rules, ruleKey],
    );

    const [draft, setDraft] = useState<PricingRuleDraft>(() => buildPricingRuleDraft(rule));

    const sortedStates = useMemo(
        () => [...organizationStates].sort((a, b) => a.localeCompare(b)),
        [organizationStates],
    );

    // An unknown rule key has nothing to edit, so fall back to the list.
    useEffect(() => {
        if (!isAddMode && !rule) navigate(sectionPath, { replace: true });
    }, [isAddMode, rule, navigate, sectionPath]);

    // Rules saved before groups were tracked carry no group, so the group is
    // recovered from the company's own name — one lookup, and only for those
    // rows; saving stores the group from then on.
    const needsGroupLookup =
        !!rule && !rule.insuranceGroupId && !draft.insuranceGroupId && !isAllCompanies(draft);
    const { data: nameSearch } = useSearchInsurance(
        needsGroupLookup ? draft.insuranceCompanyName : '',
    );
    useEffect(() => {
        if (!needsGroupLookup) return;
        const match = nameSearch?.insuranceCompanies?.find(
            (company) => company._id === draft.insuranceCompanyId,
        );
        if (match?.groupId) {
            setDraft((prev) => ({ ...prev, insuranceGroupId: match.groupId }));
        }
    }, [needsGroupLookup, nameSearch, draft.insuranceCompanyId]);

    const { data: relatedData } = useRelatedInsurance(
        isAllCompanies(draft) ? undefined : draft.insuranceGroupId,
    );
    const relatedCompanies = useMemo(() => {
        if (!relatedData?.relatedCompanies || !draft.insuranceCompanyId) return [];
        return relatedData.relatedCompanies.filter(
            (company) => company._id !== draft.insuranceCompanyId,
        );
    }, [relatedData, draft.insuranceCompanyId]);

    /**
     * A group mate that already carries a rule, offered as something to fold
     * into. Two names from one group nearly always agree the same terms with the
     * same vendors, whether that shows up while adding the second name or on a
     * company configured separately long before groups were tracked.
     */
    const { insuranceGroupId, insuranceCompanyId } = draft;
    const mergeTargetRule = useMemo(() => {
        if (!insuranceGroupId || insuranceCompanyId === LKQ_PRICING_WILDCARD) return undefined;
        return rules
            .filter(
                (candidate) =>
                    candidate.insuranceGroupId === insuranceGroupId &&
                    candidate.insuranceCompanyId !== insuranceCompanyId &&
                    // A rule this company already rides on is not something to
                    // fold into: that would point the pair at each other.
                    !candidate.includedCompanies.some(
                        (company) => company.id === insuranceCompanyId,
                    ),
            )
            .sort((a, b) => a.insuranceCompanyName.localeCompare(b.insuranceCompanyName))[0];
    }, [rules, insuranceGroupId, insuranceCompanyId]);

    // Sub companies default to riding along on a new rule: a group is normally
    // taken on whole. Seeded once per group so a refetch of the group or the
    // settings never overwrites the user's choices.
    const seededGroupRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!draft.insuranceGroupId || relatedCompanies.length === 0) return;
        if (seededGroupRef.current === draft.insuranceGroupId) return;
        seededGroupRef.current = draft.insuranceGroupId;
        setDraft((prev) => ({
            ...prev,
            subModes: seedPricingRuleSubModes(relatedCompanies, rules, rule, isAddMode),
        }));
    }, [draft.insuranceGroupId, relatedCompanies, rules, rule, isAddMode]);

    /**
     * One row per group mate, with what happens to it on save. A mate that
     * already carries its own rule says whose rule it is on, so this screen
     * never claims to own a company it will not touch.
     */
    const subCompanyRows = useMemo<SubCompanyRow[]>(
        () =>
            relatedCompanies.map((company) => ({
                insurerId: company._id,
                name: company.name,
                mode: draft.subModes[company._id] ?? 'none',
                followsName: pricingRuleFollowsName(rules, company._id),
            })),
        [relatedCompanies, draft.subModes, rules],
    );

    const namesForMode = (mode: PricingRuleSubMode): string[] =>
        relatedCompanies
            .filter((company) => draft.subModes[company._id] === mode)
            .map((company) => company.name);

    const sharedCompanyNames = namesForMode('same');
    const branchedCompanyNames = namesForMode('separate');

    useSettingsDetailCrumbs([
        {
            label: isAddMode
                ? `Add ${spec.rowNoun}`
                : draft.insuranceCompanyName || capitalize(spec.rowNoun),
        },
    ]);

    const closeFlow = () => navigate(sectionPath);

    const values = pricingRuleValues(draft);
    const isJoining = !!draft.joinParentId;
    const isSaveDisabled = isJoining ? !mergeTargetRule : !values;

    const handleSelectVendor = (supplier: SupplierSearchResult) => {
        setDraft((prev) => ({
            ...prev,
            supplierId: supplier._id,
            supplierName: supplier.title,
        }));
    };

    const handleSelectCompany = (company: InsuranceSearchResult) => {
        seededGroupRef.current = undefined;
        setDraft((prev) => ({
            ...prev,
            insuranceCompanyId: company._id,
            insuranceCompanyName: company.name,
            insuranceGroupId: company.groupId || undefined,
            parentInsuranceCompanyName: undefined,
            subModes: {},
            joinParentId: undefined,
            joinParentName: undefined,
        }));
    };

    const handleClearCompany = () => {
        seededGroupRef.current = undefined;
        setDraft((prev) => ({
            ...prev,
            insuranceCompanyId: '',
            insuranceCompanyName: '',
            insuranceGroupId: undefined,
            parentInsuranceCompanyName: undefined,
            subModes: {},
            joinParentId: undefined,
            joinParentName: undefined,
        }));
    };

    const handleToggleState = (id: string, checked: boolean) => {
        if (id === ALL_STATES_ROW) {
            setDraft((prev) => ({
                ...prev,
                states: checked ? [LKQ_PRICING_WILDCARD] : [],
            }));
            return;
        }
        setDraft((prev) => ({
            ...prev,
            // Picking a state narrows the rule, so it replaces "All" rather
            // than sitting alongside it.
            states: checked
                ? Array.from(
                      new Set([
                          ...prev.states.filter((state) => state !== LKQ_PRICING_WILDCARD),
                          id,
                      ]),
                  ).sort((a, b) => a.localeCompare(b))
                : prev.states.filter((state) => state !== id && state !== LKQ_PRICING_WILDCARD),
        }));
    };

    const saveRows = (
        next: Array<PricingEntry>,
        success: { title: string; description: string },
        failureTitle: string,
    ) => {
        updateSettings.mutate(
            {
                invoicingSettings: invoicingSettingsForApi(
                    pricingSettingsWith(spec, data, next),
                ),
            },
            {
                onSuccess: () => {
                    toast({
                        ...success,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    refetch?.();
                    closeFlow();
                },
                onError: (error) => {
                    toast({
                        title: failureTitle,
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    const handleSave = () => {
        if (isJoining) {
            if (!mergeTargetRule) return;
            saveRows(
                buildPricingRuleEntries({
                    entries,
                    draft,
                    relatedCompanies,
                    percentageOf,
                    buildEntry,
                    originalRule: rule,
                    mergeTargetRule,
                }),
                {
                    title: `${capitalize(spec.rowNoun)} merged`,
                    description: `${draft.insuranceCompanyName} now follows ${draft.joinParentName}'s ${spec.rowNoun}.`,
                },
                `Could not merge ${spec.rowNoun}`,
            );
            return;
        }

        if (!values) return;

        // Two rules for the same company, vendor and state are ambiguous rather
        // than additive, so the clash is named before anything is written.
        const remaining = rule
            ? removeLkqPricingRule(entries, rule.ruleKey, percentageOf)
            : entries;
        const companies = [
            { id: draft.insuranceCompanyId, name: draft.insuranceCompanyName },
            ...relatedCompanies
                .filter((company) => {
                    const mode = draft.subModes[company._id] ?? 'none';
                    return mode === 'same' || mode === 'separate';
                })
                .map((company) => ({ id: company._id, name: company.name })),
        ];
        const clashes = conflictingLkqPricingCompanies(remaining, companies, values);
        if (clashes.length > 0) {
            toast({
                title: `${capitalize(spec.rowNoun)} already set`,
                description: `${clashes.join(', ')} already ${
                    clashes.length === 1 ? 'has' : 'have'
                } a ${spec.rowNoun} for ${
                    isAllVendors(draft) ? 'these vendors' : draft.supplierName
                } in ${
                    isAllStates(draft) ? 'these states' : 'one of these states'
                }. Open that row to change it, or narrow this rule.`,
                status: 'error',
                duration: 6000,
                isClosable: true,
            });
            return;
        }

        const parts = [
            sharedCompanyNames.length > 0
                ? `${sharedCompanyNames.length} sub ${
                      sharedCompanyNames.length === 1 ? 'company' : 'companies'
                  } on the same ${spec.rowNoun}`
                : '',
            branchedCompanyNames.length > 0
                ? `${branchedCompanyNames.length} branched onto ${
                      branchedCompanyNames.length === 1 ? 'its own rule' : 'their own rules'
                  }`
                : '',
        ].filter(Boolean);
        saveRows(
            buildPricingRuleEntries({
                entries,
                draft,
                relatedCompanies,
                percentageOf,
                buildEntry,
                originalRule: rule,
            }),
            {
                title: `${capitalize(spec.rowNoun)} ${rule ? 'updated' : 'added'}`,
                description:
                    parts.length > 0
                        ? `${draft.insuranceCompanyName} saved with ${parts.join(' and ')}.`
                        : `${draft.insuranceCompanyName} has been saved.`,
            },
            `Could not ${rule ? 'update' : 'add'} ${spec.rowNoun}`,
        );
    };

    const handleRemove = () => {
        if (!rule) return;
        saveRows(
            removeLkqPricingRule(entries, rule.ruleKey, percentageOf),
            {
                title: `${capitalize(spec.rowNoun)} removed`,
                description: rule.includedCompanies.length
                    ? `${rule.insuranceCompanyName} and ${rule.includedCompanies.length} sub ${
                          rule.includedCompanies.length === 1 ? 'company' : 'companies'
                      } have been removed.`
                    : `${rule.insuranceCompanyName} has been removed.`,
            },
            `Could not remove ${spec.rowNoun}`,
        );
    };

    if (!isAddMode && !rule) return null;

    const hasVendor = draft.supplierId.length > 0;
    const hasCompany = draft.insuranceCompanyId.length > 0;

    const left = (
        <VStack align="stretch" spacing={6}>
            <SettingsCard title={spec.detailTitle} description={spec.percentRowDescription}>
                <SettingsRow
                    label={spec.percentRowLabel}
                    control={
                        <SettingsNumberInput
                            value={draft.percent}
                            onChange={(percent) => setDraft((prev) => ({ ...prev, percent }))}
                            min={0}
                            max={100}
                            allowDecimals
                            unit="%"
                            width="110px"
                            ariaLabel={`${spec.percentRowLabel} percentage`}
                        />
                    }
                />
            </SettingsCard>

            {spec.field === 'discountFlagExpectations' && (
                <SettingsCard title="Vehicle make">
                    <VehicleMakeField
                        value={draft.make}
                        onChange={(make) => setDraft((prev) => ({ ...prev, make }))}
                    />
                </SettingsCard>
            )}

            {(spec.field === 'lkqMarkups' || spec.field === 'lkqDiscounts') && (
                <SettingsCard title="Part kind">
                    <SettingsRow
                        label="Applies to"
                        description="Used & aftermarket keeps the original behavior for those lines. OEM, glass, and reman apply only to lines Nexsyis labels that way."
                        control={
                            <Select
                                size="sm"
                                w="220px"
                                bg="white"
                                value={draft.partKind}
                                aria-label="Part kind"
                                onChange={(event) =>
                                    setDraft((prev) => ({ ...prev, partKind: event.target.value }))
                                }
                            >
                                <option value="">Used & aftermarket</option>
                                <option value="used">Used</option>
                                <option value="aftermarket">Aftermarket</option>
                                <option value="oem">OEM</option>
                                <option value="glass">Glass</option>
                                <option value="reman">Remanufactured</option>
                                <option value="*">All part kinds</option>
                            </Select>
                        }
                    />
                    <VehicleMakeField
                        value={draft.make}
                        onChange={(make) => setDraft((prev) => ({ ...prev, make }))}
                    />
                </SettingsCard>
            )}

            {/* Vendor. Not a SettingsCard while searching: the card clips its
                content, which would cut off the results hanging below. */}
            {hasVendor ? (
                <SettingsCard title="Vendor name">
                    <SettingsRow
                        label={isAllVendors(draft) ? 'All vendors' : draft.supplierName}
                        description={
                            isAllVendors(draft)
                                ? `Every vendor is held to this ${spec.rowNoun}.`
                                : undefined
                        }
                        control={
                            <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="gray"
                                onClick={() =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        supplierId: '',
                                        supplierName: '',
                                    }))
                                }
                            >
                                Change
                            </Button>
                        }
                    />
                </SettingsCard>
            ) : (
                <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1}>
                        Vendor name
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2.5}>
                        Search the suppliers Yaxle knows and pick one, or hold every vendor to
                        this {spec.rowNoun}.
                    </Text>
                    <VendorSearchField onSelect={handleSelectVendor} autoFocus={isAddMode} />
                    <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="gray"
                        mt={2}
                        onClick={() =>
                            setDraft((prev) => ({
                                ...prev,
                                supplierId: ALL_SELECTION.id,
                                supplierName: ALL_SELECTION.name,
                            }))
                        }
                    >
                        Apply to all vendors
                    </Button>
                </Box>
            )}

            {/* Insurance company, and the group that comes with it. */}
            {hasCompany ? (
                <SettingsCard title="Insurance company">
                    <SettingsRow
                        label={
                            isAllCompanies(draft)
                                ? 'All insurance companies'
                                : draft.insuranceCompanyName
                        }
                        description={
                            isAllCompanies(draft)
                                ? `Every insurance company is held to this ${spec.rowNoun}.`
                                : draft.parentInsuranceCompanyName
                                    ? `Sub company of ${draft.parentInsuranceCompanyName}, with its own rule`
                                    : undefined
                        }
                        control={
                            <HStack spacing={2}>
                                {/* Merging sits next to the name because that is
                                    where the relationship is read: this company
                                    stops carrying a rule of its own and follows
                                    the group mate that already does. */}
                                {mergeTargetRule && !isJoining && (
                                    <Tooltip
                                        label={`Follow ${mergeTargetRule.insuranceCompanyName}'s ${spec.rowNoun} instead of keeping a rule here`}
                                        placement="top"
                                        hasArrow
                                        openDelay={300}
                                    >
                                        <Button
                                            size="xs"
                                            variant="outline"
                                            colorScheme="gray"
                                            leftIcon={
                                                <Icon
                                                    as={TbArrowMerge as React.ElementType}
                                                    boxSize={3.5}
                                                />
                                            }
                                            onClick={() =>
                                                setDraft((prev) => ({
                                                    ...prev,
                                                    joinParentId: mergeTargetRule.insuranceCompanyId,
                                                    joinParentName:
                                                        mergeTargetRule.insuranceCompanyName,
                                                }))
                                            }
                                        >
                                            Merge into {mergeTargetRule.insuranceCompanyName}
                                        </Button>
                                    </Tooltip>
                                )}
                                {/* A named company is the rule's identity, so it
                                    is swapped by removing the rule rather than
                                    renaming it. "All insurance companies" names
                                    nothing, so narrowing it to a carrier stays
                                    available while editing. */}
                                {(isAddMode || isAllCompanies(draft)) && (
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="gray"
                                        onClick={handleClearCompany}
                                    >
                                        Change
                                    </Button>
                                )}
                            </HStack>
                        }
                    />
                    {isJoining && (
                        <SettingsRow
                            label={`Follows ${draft.joinParentName}`}
                            description={`${draft.insuranceCompanyName} takes ${draft.joinParentName}'s vendor, states and percentage${
                                rule ? ', replacing the ones set here' : ''
                            }. Saving applies it.`}
                            control={
                                <Button
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="gray"
                                    onClick={() =>
                                        setDraft((prev) => ({
                                            ...prev,
                                            joinParentId: undefined,
                                            joinParentName: undefined,
                                        }))
                                    }
                                >
                                    Configure separately
                                </Button>
                            }
                        />
                    )}
                </SettingsCard>
            ) : (
                <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1}>
                        Insurance company
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2.5}>
                        Search the insurers Yaxle knows and pick one, or hold every insurance
                        company to this {spec.rowNoun}.
                    </Text>
                    <CarrierSearchField
                        onSelect={handleSelectCompany}
                        // A company can hold separate rules for separate vendors,
                        // so an existing one is worth flagging but not worth
                        // blocking; the clash is caught on save.
                        statusFor={(company) =>
                            rules.some((candidate) => candidate.insuranceCompanyId === company._id)
                                ? 'Has a rule'
                                : undefined
                        }
                        isSelectable={() => true}
                    />
                    <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="gray"
                        mt={2}
                        onClick={() =>
                            setDraft((prev) => ({
                                ...prev,
                                insuranceCompanyId: ALL_SELECTION.id,
                                insuranceCompanyName: ALL_SELECTION.name,
                                insuranceGroupId: undefined,
                                subModes: {},
                                joinParentId: undefined,
                                joinParentName: undefined,
                            }))
                        }
                    >
                        Apply to all insurance companies
                    </Button>
                    <HStack spacing={2} align="flex-start" mt={4}>
                        <Icon
                            as={TbInfoCircle as React.ElementType}
                            boxSize={4}
                            color="gray.400"
                            mt="2px"
                            flexShrink={0}
                        />
                        <Text fontSize="sm" color="gray.500">
                            Don't see the carrier you're looking for? If your insurer isn't listed,
                            contact us and we'll add them.
                        </Text>
                    </HStack>
                </Box>
            )}

            {/* A sub company has no sub companies of its own: its group is run
                from the parent's rule, so the table would only offer to reshuffle
                a group this company does not head. */}
            {hasCompany &&
                !isJoining &&
                !isAllCompanies(draft) &&
                !draft.parentInsuranceCompanyName && (
                    <Box>
                        <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1}>
                            Sub companies
                        </Text>
                        <Text fontSize="sm" color="gray.500" mb={2.5}>
                            Others in this company's group. Companies on{' '}
                            {draft.insuranceCompanyName}'s {spec.rowNoun} share one row in the
                            table; branch any that agreed a different percentage.
                        </Text>
                        <SubCompanyTable
                            parentName={draft.insuranceCompanyName}
                            sharedNoun={spec.rowNoun}
                            emptyMessage="No other companies in this group"
                            rows={subCompanyRows}
                            onModeChange={(insurerId, mode) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    subModes: { ...prev.subModes, [insurerId]: mode },
                                }))
                            }
                        />
                    </Box>
                )}

            {/* States. Skipped while folding into a parent: the rule's states
                come from the parent along with everything else. */}
            {!isJoining && (
                <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1}>
                        Applicable states
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2.5}>
                        The states this {spec.rowNoun} applies in, taken from where your shops
                        are.
                    </Text>
                    <SettingsCheckTable
                        columnLabel="STATE"
                        checkLabel="APPLIES"
                        emptyMessage="No states on your locations yet"
                        rows={[
                            ...(sortedStates.length > 0
                                ? [
                                      {
                                          id: ALL_STATES_ROW,
                                          label: 'All States',
                                          isChecked: isAllStates(draft),
                                          isEmphasis: true,
                                      },
                                  ]
                                : []),
                            ...sortedStates.map((state) => ({
                                id: state,
                                label: state,
                                isChecked: isAllStates(draft) || draft.states.includes(state),
                            })),
                        ]}
                        onToggle={handleToggleState}
                    />
                </Box>
            )}
        </VStack>
    );

    return (
        <SettingsFlow
            steps={[
                {
                    id: 'rule',
                    label: isAddMode
                        ? `Add ${spec.rowNoun}`
                        : capitalize(spec.rowNoun),
                },
            ]}
            activeStepId="rule"
            onCancel={closeFlow}
            onSave={handleSave}
            saveLabel={isAddMode ? 'Add Rule' : 'Save Rule'}
            isSaveDisabled={isSaveDisabled}
            isSaving={updateSettings.isPending}
            actions={
                rule ? (
                    <Button
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        onClick={handleRemove}
                        isDisabled={updateSettings.isPending}
                    >
                        Remove Rule
                    </Button>
                ) : undefined
            }
        >
            <SettingsFlowSplit
                leftWidth="45%"
                leftTitle={
                    isAddMode ? `Add ${spec.rowNoun}` : `${capitalize(spec.rowNoun)} rule`
                }
                left={left}
                rightTitle="Rule"
                rightDescription="What Yaxle will look for, in the order it reads it."
                right={
                    <PricingRuleSteps
                        draft={draft}
                        spec={spec}
                        sharedCompanyNames={sharedCompanyNames}
                        branchedCompanyNames={branchedCompanyNames}
                        mergeTargetRule={mergeTargetRule}
                    />
                }
            />
        </SettingsFlow>
    );
};

const OTHER_MAKE = '__other_make__';

/** Known makes from the `makes` collection, or a typed name when the vehicle is not in that list. */
function VehicleMakeField({
    value,
    onChange,
}: {
    value: string;
    onChange: (make: string) => void;
}) {
    const { data } = useGetMakes();
    const makes = data?.makes ?? [];
    const trimmed = value.trim();
    const knownMake = makes.find((make) => make.name.toLowerCase() === trimmed.toLowerCase());
    const known = Boolean(knownMake);
    const [otherOpen, setOtherOpen] = useState(false);
    const showOther = otherOpen || (trimmed !== '' && !known);
    const description = !trimmed
        ? 'Leave blank to match every make. Pick a make, or choose Other and type one that is not in the list.'
        : known
            ? 'Matches the vehicle make stored for the Nexsyis purchase-order VIN.'
            : 'This make is not in the makes list. The typed name is matched to the vehicle on the Nexsyis purchase order.';

    return (
        <SettingsRow
            label="Vehicle make"
            description={description}
            control={
                <VStack align="stretch" spacing={2} width="220px">
                    <Select
                        size="sm"
                        w="220px"
                        bg="white"
                        value={showOther ? OTHER_MAKE : (knownMake?.name ?? trimmed)}
                        aria-label="Vehicle make"
                        onChange={(event) => {
                            const next = event.target.value;
                            if (next === OTHER_MAKE) {
                                setOtherOpen(true);
                                if (known) onChange('');
                                return;
                            }
                            setOtherOpen(false);
                            onChange(next);
                        }}
                    >
                        <option value="">All makes</option>
                        {makes.map((make) => (
                            <option key={make._id} value={make.name}>
                                {make.name}
                            </option>
                        ))}
                        <option value={OTHER_MAKE}>Other</option>
                    </Select>
                    {showOther && (
                        <TextField
                            size="sm"
                            w="220px"
                            bg="white"
                            value={known ? '' : value}
                            placeholder="Type a make"
                            aria-label="Other vehicle make"
                            onChange={(event) => onChange(event.target.value)}
                        />
                    )}
                </VStack>
            }
        />
    );
}

export interface PricingRuleDetailSectionProps {
    /** The settings section the rule belongs to. */
    sectionId: PricingSectionId;
    /** The route's detail segment: `<tab>~<ruleKey>`. */
    rowId: string;
    data: InvoicingSettings;
    organizationStates: string[];
    refetch?: (options?: RefetchOptions) => Promise<QueryObserverResult<unknown, Error>>;
}

/**
 * Resolves the route's one segment into the table it names, so the editor below
 * can take a table for granted. A segment that names no tab of this section is
 * a link to a row that no longer exists, which belongs back at the list.
 */
const PricingRuleDetailSection = ({
    sectionId,
    rowId,
    data,
    organizationStates,
    refetch,
}: PricingRuleDetailSectionProps) => {
    const navigate = useNavigate();
    const sectionPath = `/settings/${sectionId}`;
    const parsed = useMemo(() => parsePricingRowId(rowId), [rowId]);
    const spec = useMemo(
        () => (parsed ? pricingTableSpec(sectionId, parsed.tabId) : undefined),
        [parsed, sectionId],
    );

    useEffect(() => {
        if (!spec) navigate(sectionPath, { replace: true });
    }, [spec, navigate, sectionPath]);

    if (!spec || !parsed) return null;

    return (
        <PricingRuleEditor
            spec={spec}
            ruleKey={parsed.ruleKey}
            sectionPath={sectionPath}
            data={data}
            organizationStates={organizationStates}
            refetch={refetch}
        />
    );
};

export default PricingRuleDetailSection;
